import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createIndexActor, createShopActor } from "../lib/actors";
import { isOwnedShopId, lastShopKey } from "../lib/access";
import type {
  ShopActorSubclass,
  ShopMeta,
  ShopRecord,
} from "../lib/types";
import { useAuth } from "./AuthContext";

interface WorkspaceValue {
  shops: ShopRecord[];
  loading: boolean;
  error: string;
  selectShop: (shopId: string) => Promise<"ok" | "denied" | "error">;
  actor: ShopActorSubclass | null;
  meta: ShopMeta | null;
  activeShop: ShopRecord | null;
  reload: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { identity } = useAuth();
  const [shops, setShops] = useState<ShopRecord[]>([]);
  const [actor, setActor] = useState<ShopActorSubclass | null>(null);
  const [meta, setMeta] = useState<ShopMeta | null>(null);
  const [activeShop, setActiveShop] = useState<ShopRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!identity) {
      setShops([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const index = await createIndexActor(identity);
      const result = await index.getMyAccess();
      if ("err" in result) {
        if ("Anonymous" in result.err) {
          setShops([]);
          return;
        }
        throw result.err;
      }
      setShops(result.ok.ownedShops);
    } catch {
      setError("Could not load your shops. Check your connection and retry.");
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [identity]);

  useEffect(() => {
    setActor(null);
    setMeta(null);
    setActiveShop(null);
    void reload();
  }, [reload]);

  const selectShop = useCallback(
    async (shopId: string): Promise<"ok" | "denied" | "error"> => {
      if (!identity) return "denied";
      if (!isOwnedShopId(shops, shopId)) {
        setActor(null);
        setMeta(null);
        setActiveShop(null);
        return "denied";
      }
      const shop = shops.find((candidate) => candidate.id.toText() === shopId)!;
      setLoading(true);
      setError("");
      try {
        const nextActor = await createShopActor(shopId, identity);
        const result = await nextActor.getShopMeta();
        if ("err" in result) throw result.err;
        if (result.ok.owner.toText() !== identity.getPrincipal().toText()) {
          setError("Owner access is no longer available for this shop.");
          return "denied";
        }
        setActor(nextActor);
        setMeta(result.ok);
        setActiveShop(shop);
        localStorage.setItem(lastShopKey(identity.getPrincipal().toText()), shopId);
        return "ok";
      } catch (caught) {
        const denied =
          typeof caught === "object" &&
          caught !== null &&
          ("NotOwner" in caught ||
            "NotAuthorized" in caught ||
            "Anonymous" in caught);
        setError(
          denied
            ? "Owner access is no longer available for this shop."
            : "Could not reach the shop canister. Check your connection and retry.",
        );
        setActor(null);
        setMeta(null);
        setActiveShop(null);
        return denied ? "denied" : "error";
      } finally {
        setLoading(false);
      }
    },
    [identity, shops],
  );

  const value = useMemo(
    () => ({
      shops,
      loading,
      error,
      selectShop,
      actor,
      meta,
      activeShop,
      reload,
    }),
    [shops, loading, error, selectShop, actor, meta, activeShop, reload],
  );
  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (!value) {
    throw new Error("useWorkspace must be used inside WorkspaceProvider");
  }
  return value;
}
